// call-citation-sources.ts — the ONE owner of the `['call-citation-sources']`
// cache key.
//
// THE RULE: a query key root is declared in exactly one module. See
// `apps/web/src/lib/agent-rows/hooks.ts` for the full statement of the rule,
// and `apps/web/src/lib/__tests__/query-key-ownership.test.ts` for the guard.
//
// Two surfaces read a call's transcript lines, for two different reasons, and
// they must share one cache entry:
//   - `<CitedSummary>` resolves each `[^N]` marker in an agent-written body to
//     the verbatim line it cites.
//   - The Calls & Meetings detail pane RENDERS those lines as the transcript,
//     so a call is read without the list payload carrying every transcript in
//     the book.
// One key, one shape, one fetch: opening a call the reader already has
// citations for costs no second request.
//
// `GET /api/desk/calls/:callId/citation-sources` returns the call's transcript
// lines through the desk-pages contract, persona-gated like every other read.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { CitationSource } from '@/lib/meeting-note-citations';

export type { CitationSource } from '@/lib/meeting-note-citations';

/** The transcript lines of one call. `null` disables the read. */
export function useCallCitationSources(callId: string | null): UseQueryResult<CitationSource[]> {
    return useQuery<CitationSource[]>({
        queryKey: ['call-citation-sources', callId],
        enabled: Boolean(callId),
        staleTime: 5 * 60_000,
        queryFn: async () => {
            const res = await api.deskPages.getCallCitationSources({
                params: { callId: callId as string },
            });
            if (res.status !== 200) throw new Error(`citation sources ${res.status}`);
            return res.body.sources;
        },
    });
}
